import React, { useEffect, useState } from "react";
import axiosInstance from "../axios/axios";
import Loader from "./Loader";
import { useTranslate } from "./TranslationText";
import NetaqInput from "./NetaqInput";
import { convertToCamelCase } from "../helper/convertToCamelCase";
import EahsForm from "./EahsForm";
import NewActionButton from "./ActionButtons/NewActionButton";
import { Link } from "react-router-dom";
import { formatDateTimeToString } from "../helper/formatDateTimeToString";
import { eformInput, noDataCard } from "../common/cssclasses";

const PAGE_SIZE = 20;

function toApiDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d)) return null;
  return d.toISOString().split("T")[0];
}

export default function PaymentHistory() {
  const { translate } = useTranslate();

  const [currentPayments, setCurrentPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paymentLoaded, setPaymentsLoaded] = useState(false);  // controls full-page Loader (initial load only)
  const [tableLoading, setTableLoading] = useState(false);     // controls table-only spinner (filter/page change)

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Filter states
  const [referenceNumber, setReferenceNumber] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [paidStatus, setPaidStatus] = useState("paid");
  const [fromDateEvent, setFromDateEvent] = useState(null);
  const [toDateEvent, setToDateEvent] = useState(null);

  async function fetchPayments(page = 1, filters = {}, isInitialLoad = false) {
    if (isInitialLoad) {
      setPaymentsLoaded(false); // show full-page Loader
    } else {
      setTableLoading(true);    // show spinner only in table area
    }
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams();
      params.append("skip", skip);
      params.append("take", PAGE_SIZE);
      if (filters.refranceNumber) params.append("refranceNumber", filters.refranceNumber);
      if (filters.from)           params.append("from", filters.from);
      if (filters.to)             params.append("to", filters.to);
      if (filters.status)         params.append("status", filters.status);

      const response = await axiosInstance.get(
        `payment/getpaymenttransactionbyuserid?${params.toString()}`
      );

      if (response.data.responseCode === "200") {
        setCurrentPayments(response.data.result ?? []);
        const total =
          response.data.paginationInfo?.totalCount ??
          response.data.totalCount ??
          (response.data.result?.length ?? 0);
        setTotalCount(total);
      } else {
        setCurrentPayments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error loading payment history", error);
      setCurrentPayments([]);
      setTotalCount(0);
    } finally {
      setPaymentsLoaded(true);
      setTableLoading(false);
    }
  }

  const buildFilters = (refNum, from, to, status) => ({
    refranceNumber: refNum?.trim() || undefined,
    from:   toApiDate(from)  || undefined,
    to:     toApiDate(to)    || undefined,
    status: status           || undefined,
  });

  useEffect(() => {
    fetchPayments(1, {}, true); // initial load — show full Loader
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (newRef, newFrom, newTo, newStatus) => {
    const filters = buildFilters(newRef, newFrom, newTo, newStatus);
    setCurrentPage(1);
    fetchPayments(1, filters);
  };

  const handleReset = () => {
    setFromDateEvent(null);
    setToDateEvent(null);
    setFromDate(null);
    setToDate(null);
    setReferenceNumber("");
    setPaidStatus("paid");
    setCurrentPage(1);
    fetchPayments(1, {});
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    fetchPayments(newPage, buildFilters(referenceNumber, fromDate, toDate, paidStatus));
    window.scrollTo(0, 0);
  };

  return (
    <>
      {paymentLoaded ? (
        <div className="col-lg-12 col-md-12 col-sm-12">

          {/* Filter controls — same UI as before */}
          <EahsForm className={'mb-main'} title={translate("web.paymentHistory", "Payment History")}>
            <div className={eformInput}>
              <NetaqInput
                type={"text"}
                value={referenceNumber}
                onChange={(e) => {
                  setReferenceNumber(e.target.value);
                  handleFilterChange(e.target.value, fromDate, toDate, paidStatus);
                }}
                label={translate("web.refranceNumber", "Refrance Number")}
                placeholder={translate("web.refranceNumber", "Refrance Number")}
                name={translate("web.refranceNumber", "Refrance Number")}
              />
            </div>
            <div className={eformInput}>
              <NetaqInput
                type="date"
                value={fromDateEvent}
                onChange={(e) => {
                  setFromDateEvent(e);
                  const d = e && e.toDate ? e.toDate() : null;
                  setFromDate(d);
                  handleFilterChange(referenceNumber, d, toDate, paidStatus);
                }}
                label={translate("web.from", "From ")}
                name={translate("web.from", "From ")}
              />
            </div>
            <div className={eformInput}>
              <NetaqInput
                type="date"
                minDate={fromDateEvent}
                value={toDateEvent}
                onChange={(e) => {
                  setToDateEvent(e);
                  const d = e && e.toDate ? e.toDate() : null;
                  setToDate(d);
                  handleFilterChange(referenceNumber, fromDate, d, paidStatus);
                }}
                label={translate("web.to", "To ")}
                name={translate("web.to", "To ")}
              />
            </div>

            <div className={eformInput}>
              <label className="capatalize_text">
                {translate("web.status", "Status")}
              </label>
              <select
                className="form-control"
                value={paidStatus}
                onChange={(e) => {
                  setPaidStatus(e.target.value);
                  handleFilterChange(referenceNumber, fromDate, toDate, e.target.value);
                }}
              >
                <option value="">{translate("web.all", "All")}</option>
                <option value="paid">{translate("web.completed", "Completed")}</option>
                <option value="notPaid">{translate("web.notCompleted", "Not Completed")}</option>
              </select>
            </div>
            <div className="col-lg-2 col-md-2 col-sm-12 ms-auto" onClick={handleReset}>
              <NewActionButton
                classNames="btn-outline-secondary"
                isNoMargin
                text={translate("web.reset", "Reset")}
              />
            </div>
          </EahsForm>

          {/* Table area — shows spinner on filter/page change, keeps filter section visible */}
          {tableLoading ? (
            <div className="text-center w-100 py-5">
              <i className="fas fa-spinner fa-spin fa-3x color-eahsred"></i>
            </div>
          ) : currentPayments.length > 0 ? (
            <div className="scrolableTable">
              <table className="table table-striped table-bordered rtl horseResultTable mb-main">
                <thead className="theadHorseSelect">
                  <tr>
                    <th className="text-center">{translate("web.refranceNumber", "Refrance Number")}</th>
                    <th className="text-center">{translate("web.date", "Date")}</th>
                    <th className="text-center">{translate("web.amount", "Amount")}</th>
                    <th className="text-center">{translate("web.paymentReason", "Payment Reason")}</th>
                    <th className="text-center">{translate("web.status", "Status")}</th>
                    <th className="text-center">{translate("web.bankDecision", "Bank Decision")}</th>
                    <th className="text-center">{translate("web.paymentType", "Payment Type")}</th>
                    <th className="text-center">{translate("web.receiptPerformaInvoice", "Receipt - Performa Invoice")}</th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {currentPayments.map((item, index) => (
                    <tr key={index}>
                      <td className="rtl">{item.refranceNumber}</td>
                      <td className="rtl">{formatDateTimeToString(item.createdDate)}</td>
                      <td className="rtl">
                        {item?.amount?.toLocaleString("en-US") + " " + item.currency}
                      </td>
                      <td className="rtl">
                        {translate(
                          `web.Payment${convertToCamelCase(item.paymentType)}`,
                          convertToCamelCase(item.paymentType)
                        )}
                      </td>
                      <td className={`rtl text-center ${item.isPaid ? "text-success" : "text-danger"}`}>
                        {item.isPaid
                          ? translate("web.completed", "Completed")
                          : translate("web.notCompleted", "Not Completed")}
                      </td>
                      <td className="rtl" style={{ maxWidth: 75 }}>
                        {item.decisionText}{" "}
                        {item?.decisionText?.toLowerCase() !== "accept" && (
                          <>{item?.messageText}</>
                        )}
                      </td>
                      <td className="rtl" style={{ maxWidth: 75 }}>
                        {item.paymentMethod == null || item.paymentMethod === "Online"
                          ? translate("web.onlinePayment", "Online Payment")
                          : translate("web.creditPayment", "Credit Payment")}
                      </td>
                      <td className="rtl">
                        {!!item?.receiptUrl && (
                          <Link className="btn btn-danger my-1 w-100" target="_blank" to={item?.receiptUrl}>
                            <i className="fa-solid fa-download"></i> {translate('web.recipt', 'Recipt')}
                          </Link>
                        )}
                        {!!item?.vatUrl && (
                          <Link className="btn btn-danger my-1 w-100" target="_blank" to={item?.vatUrl}>
                            <i className="fa-solid fa-receipt"></i>
                            {translate('web.vatInvoice', 'VAT Invoice')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination buttons — same UI as before */}
              <div className="pagination-buttons rtl">
                <NewActionButton
                  classNames="btn-outline-danger w-unset"
                  key={'prev'}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  text={translate("web.previous", "Previous")}
                />
                <NewActionButton
                  classNames="btn-danger w-unset ms-2"
                  key={'pages'}
                  text={`${currentPage} / ${totalPages}`}
                />
                <NewActionButton
                  key={'next'}
                  classNames="btn-outline-danger w-unset ms-2"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  text={translate("web.next", "Next")}
                />
              </div>
            </div>
          ) : (
            <div className={noDataCard}>
              {translate("web.nodataFound", "No Result Found")}
            </div>
          )}
        </div>
      ) : (
        <Loader />
      )}
    </>
  );
}
